
import Link from '@/components/compat/Link';
import { useMemo, type ReactNode } from 'react';
import { HeroBackdropLayers, GsImage, heroDescriptionClass, heroDescriptionDetailClass } from '@/components/help-center/latest-power-shared';
import { AceIcon } from '@/components/help-center/aces/AceIcon';
import { AceStats } from '@/components/help-center/aces/AceStats';
import { AcesLogo } from '@/components/help-center/aces/AcesLogo';
import {
  ACE_ICON_SHELL_STYLE,
  ACE_SUBSCRIBE_BUTTON_STYLE,
  aceCtaSeparatorClass,
  aceSubscribeButtonClass,
  formatAceDisplayName,
} from '@/components/help-center/aces/ace-shared';
import { SmileySvgBadgeIcon, PawnBadgeIcon } from '@/components/help-center/powers/power-gallery-view';
import { ACES_STORE_URL } from '@/lib/ace-avatar-frames';
import {
  ACES_PAGE,
  getAceListingStats,
  type AceInfo,
} from '@/lib/aces-data';
import { getPowerBySlug, mergePowerWithMockExtras } from '@/lib/powers-data';
import { stripHtmlToText } from '@/lib/cms-rich-helpers';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const aceIconShellStyle = {
  ...ACE_ICON_SHELL_STYLE,
  boxShadow:
    '0 0 0 1.5px #5c4010, 0 0 0 2.5px #7a5218, 8px 12px 28px rgba(0,0,0,0.55)',
};

type AceHeroListingProps = {
  variant: 'listing';
  listingStats?: import('@/lib/aces-data').AceListingStats;
};

type AceHeroDetailProps = {
  variant: 'detail';
  ace: AceInfo;
  /** Slot na linha dos breadcrumbs (ex.: botão Editar). */
  editAction?: ReactNode;
};

export type AceHeroProps = AceHeroListingProps | AceHeroDetailProps;

export function AceHero(props: AceHeroProps) {
  const isListing = props.variant === 'listing';
  const stats = isListing
    ? (props.variant === 'listing' && props.listingStats) || getAceListingStats()
    : null;
  const ace = props.variant === 'detail' ? props.ace : null;
  const aceDisplayName = ace ? formatAceDisplayName(ace.slug) : '';

  const aceGalleryCounts = useMemo(() => {
    if (!ace) return { smilies: 0, pawns: 0 };

    const base = getPowerBySlug(ace.slug);
    if (!base) return { smilies: 0, pawns: 0 };

    const merged = mergePowerWithMockExtras(base);
    return {
      smilies: merged.smilies.length,
      pawns: merged.pawns.length,
    };
  }, [ace]);

  return (
    <section className="relative z-30 overflow-x-hidden bg-[#030023]">
      <HeroBackdropLayers className="absolute inset-0" />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-amber-500/8 via-transparent to-indigo-600/10"
        aria-hidden
      />

      <div className="relative z-10 mx-auto max-w-7xl px-6 pb-16 pt-32 md:pb-20 md:pt-40">
        {!isListing && ace ? (
          <div className="mb-10 flex items-center justify-between gap-3">
            <nav className="flex min-w-0 flex-wrap items-center gap-1.5 font-poppins text-sm">
              <Link href="/" className="text-white/40 transition-colors hover:text-white/70">
                Início
              </Link>
              <ChevronRight className="h-3.5 w-3.5 text-white/25" aria-hidden />
              <Link href="/aces" className="text-white/40 transition-colors hover:text-white/70">
                Aces
              </Link>
              <ChevronRight className="h-3.5 w-3.5 text-white/25" aria-hidden />
              <span className="text-white/80">{aceDisplayName}</span>
            </nav>
            {'editAction' in props && props.editAction ? (
              <div className="shrink-0">{props.editAction}</div>
            ) : null}
          </div>
        ) : null}

        <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(280px,420px)] lg:gap-12">
          <div className="min-w-0">
            {isListing ? (
              <>
                <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-amber-300/25 bg-amber-400/10 px-4 py-1.5 font-poppins text-sm font-semibold text-amber-100 backdrop-blur-sm">
                  <AceIcon className="h-3.5 w-3.5 text-amber-300" />
                  Assinaturas premium
                </span>
                <AcesLogo className="mb-5" />
                <p className={cn('max-w-2xl', heroDescriptionClass)}>
                  {ACES_PAGE.description}
                </p>
              </>
            ) : ace ? (
              <div className="flex flex-col gap-8">
                <div className="flex items-start gap-6">
                  <div
                    className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl"
                    style={aceIconShellStyle}
                  >
                    <GsImage
                      code={ace.smileyCode}
                      className="flex h-full w-full items-center justify-center"
                      imgClassName="max-h-[72px] max-w-[72px] object-contain"
                    />
                  </div>

                  <div className="min-w-0 flex flex-col gap-3 pt-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 font-poppins text-xs font-semibold text-sky-100">
                        {ace.subscriptionLabel}
                      </span>
                      {ace.powerId ? (
                        <span
                          className="inline-flex items-center rounded-full px-2.5 py-0.5 font-poppins text-[11px] font-bold tabular-nums"
                          style={{
                            background: 'linear-gradient(135deg, #3d2a06 0%, #2a1d04 100%)',
                            color: '#fcd34d',
                            boxShadow: '0 0 0 1.5px #7a5218',
                          }}
                        >
                          ID {ace.powerId}
                        </span>
                      ) : null}
                    </div>

                    <h1
                      className="font-poppins text-4xl font-bold leading-tight tracking-tight text-white md:text-5xl"
                      style={{
                        textShadow:
                          '0 0 28px rgba(255,255,255,0.18), 0 0 8px rgba(255,255,255,0.08)',
                      }}
                    >
                      {aceDisplayName}
                    </h1>
                  </div>
                </div>

                <p className={cn('max-w-2xl', heroDescriptionDetailClass)}>
                  {stripHtmlToText(ace.shortDescription)}
                </p>

                <div className="flex flex-wrap items-center gap-2.5">
                  {aceGalleryCounts.smilies > 0 ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] bg-white/[0.06] px-3 py-1.5 font-poppins text-xs font-semibold text-blue-100/70 backdrop-blur-sm">
                      <SmileySvgBadgeIcon className="text-blue-100/70" />
                      {aceGalleryCounts.smilies} smilies
                    </span>
                  ) : null}
                  {aceGalleryCounts.pawns > 0 ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-white/[0.1] bg-white/[0.06] px-3 py-1.5 font-poppins text-xs font-semibold text-blue-100/70 backdrop-blur-sm">
                      <PawnBadgeIcon className="text-blue-100/70" />
                      <span className="leading-none">
                        {aceGalleryCounts.pawns} peões
                      </span>
                    </span>
                  ) : null}
                  {aceGalleryCounts.smilies > 0 || aceGalleryCounts.pawns > 0 ? (
                    <span className={aceCtaSeparatorClass} aria-hidden />
                  ) : null}
                  <a
                    href={ACES_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={aceSubscribeButtonClass}
                    style={ACE_SUBSCRIBE_BUTTON_STYLE}
                  >
                    <AceIcon className="h-3.5 w-3.5 shrink-0" />
                    Assine agora
                  </a>
                  <span className={aceCtaSeparatorClass} aria-hidden />
                  <p className="font-inter text-sm text-amber-200/75">{ace.renewalLabel}</p>
                </div>
              </div>
            ) : null}
          </div>

          {isListing && stats ? (
            <AceStats variant="listing" stats={stats} className="lg:mt-2" />
          ) : ace ? (
            <AceStats variant="detail" ace={ace} className="lg:mt-2" />
          ) : null}
        </div>
      </div>
    </section>
  );
}
